import React from 'react';
import InternalPageLayout from '../components/InternalPageLayout';
import { SectionHeading } from '../components/ContentComponents';
import HTMLContent from '../components/HTMLContent';
import Gallery from '../components/Gallery';
import { useService } from '../hooks/useService';
import { Sprout, Phone, Mail, Download, ShoppingCart } from 'lucide-react';

export default function NurseryPage() {
  const { data, loading, error } = useService('nursery');

  if (loading) {
    return (
      <InternalPageLayout title="Nursery" breadcrumbs={[{ label: 'Services' }, { label: 'Nursery' }]}>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0E6D2B] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </InternalPageLayout>
    );
  }

  if (error || !data) {
    return (
      <InternalPageLayout title="Nursery" breadcrumbs={[{ label: 'Services' }, { label: 'Nursery' }]}>
        <div className="text-center py-12">
          <p className="text-red-600">{error || 'No data available'}</p>
        </div>
      </InternalPageLayout>
    );
  }

  return (
    <InternalPageLayout
      title={data.page_title}
      subtitle="Quality planting material of improved varieties"
      breadcrumbs={[
        { label: 'Services', href: '/about' },
        { label: data.page_title }
      ]}
      heroImage={data.banner_image}
    >
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Intro */}
          {data.intro_html && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <HTMLContent content={data.intro_html} />
            </div>
          )}

          {/* Gallery */}
          {data.gallery && data.gallery.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <SectionHeading icon={Sprout} title="Nursery Gallery" />
              <Gallery images={data.gallery} columns={3} />
            </div>
          )}

          {/* Available Plants */}
          {data.plants && data.plants.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <SectionHeading icon={ShoppingCart} title="Available Plants" />
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F8EF]">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-800">Plant</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-800">Price</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-800">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.plants.map((plant, index) => (
                      <tr key={index} className="border-t border-gray-200">
                        <td className="px-4 py-3 text-gray-700">{plant.plant}</td>
                        <td className="px-4 py-3 text-gray-700 font-semibold">{plant.price}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                            plant.status === 'Available' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {plant.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* How to Order */}
          {data.order_steps && data.order_steps.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">How to Order</h3>
              <ol className="space-y-3">
                {data.order_steps.map((step, index) => (
                  <li key={index} className="flex gap-3 text-gray-700">
                    <span className="flex-shrink-0 w-6 h-6 bg-[#0E6D2B] text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </span>
                    <span className="text-sm">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Downloads */}
          {(data.allocation_pdf || data.allotment_pdf) && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Downloads</h3>
              <div className="flex flex-wrap gap-4">
                {data.allocation_pdf && (
                  <a
                    href={data.allocation_pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#0E6D2B] text-white rounded-lg hover:bg-[#0A5421] transition-colors"
                  >
                    <Download className="w-5 h-5" />
                    Allocation Notice
                  </a>
                )}
                {data.allotment_pdf && (
                  <a
                    href={data.allotment_pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#0E6D2B] text-white rounded-lg hover:bg-[#0A5421] transition-colors"
                  >
                    <Download className="w-5 h-5" />
                    Allotment Schedule
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact */}
          {data.contact && (
            <div className="bg-[#F4F8EF] rounded-xl p-6">
              <h3 className="font-bold text-gray-800 mb-4">Contact Information</h3>
              <div className="space-y-3 text-sm">
                {data.contact.name && <p className="font-semibold text-gray-800">{data.contact.name}</p>}
                {data.contact.role && <p className="text-gray-600">{data.contact.role}</p>}
                {data.contact.phone && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone className="w-4 h-4 text-[#0E6D2B]" />
                    {data.contact.phone} {data.contact.ext && `(Ext: ${data.contact.ext})`}
                  </div>
                )}
                {data.contact.email && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="w-4 h-4 text-[#0E6D2B]" />
                    <a href={`mailto:${data.contact.email}`} className="hover:text-[#0E6D2B]">
                      {data.contact.email}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Booking Link */}
          {data.booking_url && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="font-bold text-gray-800 mb-3">Book Plants</h3>
              <a
                href={data.booking_url}
                className="block text-center px-6 py-3 bg-[#0E6D2B] text-white rounded-lg hover:bg-[#0A5421] transition-colors"
              >
                Plant Booking Form
              </a>
            </div>
          )}
        </div>
      </div>
    </InternalPageLayout>
  );
}
